/**
 * Platform detection utilities for PantherAccess
 *
 * This module provides utilities to detect whether the app is running
 * in Electron (desktop) or a regular web browser, and to get platform-specific
 * information.
 */

// Type declaration for the Electron API exposed via preload script
declare global {
  interface Window {
    electronAPI?: {
      platform: {
        isElectron: boolean;
        platform: 'darwin' | 'win32' | 'linux';
        arch: string;
        version: string;
        nodeVersion: string;
        chromeVersion: string;
      };
      isElectron: () => boolean;
      getPlatform: () => 'darwin' | 'win32' | 'linux';
      isMac: () => boolean;
      isWindows: () => boolean;
      isLinux: () => boolean;
      minimizeWindow: () => Promise<void>;
      maximizeWindow: () => Promise<void>;
      closeWindow: () => Promise<void>;
      isMaximized: () => Promise<boolean>;
      onWindowStateChanged: (callback: () => void) => () => void;
      checkFfmpeg: () => Promise<{
        available: boolean;
        path?: string;
        version?: string;
        error?: string;
      }>;
      captureIPCameraSnapshot: (config: {
        protocol: 'rtsp' | 'http' | 'mjpeg';
        ipAddress: string;
        port: number;
        username?: string;
        password?: string;
        rtspPath?: string;
        snapshotPath?: string;
      }) => Promise<{
        success: boolean;
        data?: string;
        error?: string;
      }>;
      testIPCameraConnection: (config: {
        protocol: 'rtsp' | 'http' | 'mjpeg';
        ipAddress: string;
        port: number;
        username?: string;
        password?: string;
      }) => Promise<{
        success: boolean;
        message?: string;
        error?: string;
      }>;
      // Continuous streaming (RTSP or HTTP/MJPEG via ffmpeg)
      startIPCameraStream: (config: {
        streamId: string;
        protocol?: 'rtsp' | 'http';
        ipAddress: string;
        port: number;
        username?: string;
        password?: string;
        rtspPath?: string;
        snapshotPath?: string;
        fps?: number;
        quality?: 'low' | 'medium' | 'high' | 'ultra';
        /** Tag so the owning UI can stop all of its streams in one call. */
        ownerGroup?: string;
        /** Opt out of the concurrency cap and the same-camera dedup kill. */
        allowConcurrent?: boolean;
      }) => Promise<{
        success: boolean;
        streamId?: string;
        error?: string;
      }>;
      stopIPCameraStream: (streamId: string) => Promise<{
        success: boolean;
        error?: string;
      }>;
      stopIPCameraStreamGroup?: (ownerGroup: string) => Promise<{
        success: boolean;
        stopped?: number;
        error?: string;
      }>;
      keepaliveIPCameraStream: (streamId: string) => Promise<{
        success: boolean;
        error?: string;
      }>;
      onIPCameraFrame: (callback: (data: {
        streamId: string;
        frame: string;
        timestamp: number;
      }) => void) => () => void;
      onIPCameraStreamEnded: (callback: (data: { streamId: string }) => void) => () => void;
      onIPCameraStreamReconnecting?: (callback: (data: {
        streamId: string;
        attempt: number;
        maxAttempts: number;
        nextRetryMs: number;
      }) => void) => () => void;
      onIPCameraStreamReconnected?: (callback: (data: {
        streamId: string;
        attemptsTaken: number;
      }) => void) => () => void;
      onIPCameraStreamError?: (callback: (data: {
        streamId: string;
        error: string;
        message: string;
      }) => void) => () => void;
      extractOCR: (imageBase64: string) => Promise<{
        success: boolean;
        text?: string;
        confidence?: number;
        error?: string;
      }>;
      cleanupRtspConnections?: () => Promise<void>;
      deviceHttpRequest: (config: {
        url: string;
        method: 'GET' | 'POST' | 'PUT' | 'DELETE';
        headers?: Record<string, string>;
        body?: any;
        timeout?: number;
        digestAuth?: {
          username: string;
          password: string;
        };
      }) => Promise<{
        success: boolean;
        data?: any;
        error?: string;
        timeMs: number;
      }>;
      deviceQuickProbe: (host: string, port: number, timeoutMs?: number) => Promise<boolean>;
    };
  }
}

/**
 * Check if the app is running in Electron
 */
export const isElectron = (): boolean => {
  return typeof window !== 'undefined' && !!window.electronAPI?.isElectron?.();
};

/**
 * Check if the app is running in a web browser
 */
export const isWeb = (): boolean => {
  return !isElectron();
};

/**
 * Get the current platform
 * Returns 'mac', 'win', 'linux', or 'web'
 */
export type Platform = 'mac' | 'win' | 'linux' | 'web';

export const getPlatform = (): Platform => {
  if (!isElectron()) return 'web';

  const platform = window.electronAPI?.getPlatform?.();
  if (platform === 'darwin') return 'mac';
  if (platform === 'win32') return 'win';
  if (platform === 'linux') return 'linux';
  return 'web';
};

/**
 * Check if running on macOS (Electron only)
 */
export const isMac = (): boolean => {
  return isElectron() && (window.electronAPI?.isMac?.() ?? false);
};

/**
 * Check if running on Windows (Electron only)
 */
export const isWindows = (): boolean => {
  return isElectron() && (window.electronAPI?.isWindows?.() ?? false);
};

/**
 * Check if running on Linux (Electron only)
 */
export const isLinux = (): boolean => {
  return isElectron() && (window.electronAPI?.isLinux?.() ?? false);
};

/**
 * Get detailed platform information
 */
export interface PlatformInfo {
  isElectron: boolean;
  isWeb: boolean;
  platform: Platform;
  electronVersion?: string;
  nodeVersion?: string;
  chromeVersion?: string;
  arch?: string;
}

export const getPlatformInfo = (): PlatformInfo => {
  if (!isElectron()) {
    return {
      isElectron: false,
      isWeb: true,
      platform: 'web',
    };
  }

  const electronPlatform = window.electronAPI?.platform;

  return {
    isElectron: true,
    isWeb: false,
    platform: getPlatform(),
    electronVersion: electronPlatform?.version,
    nodeVersion: electronPlatform?.nodeVersion,
    chromeVersion: electronPlatform?.chromeVersion,
    arch: electronPlatform?.arch,
  };
};

/**
 * Log platform information (useful for debugging)
 */
export const logPlatformInfo = (): void => {
  const info = getPlatformInfo();
  console.log('[Platform] Running in:', info.isElectron ? 'Electron' : 'Web Browser');
  console.log('[Platform] Platform:', info.platform);
  if (info.isElectron) {
    console.log('[Platform] Electron:', info.electronVersion);
    console.log('[Platform] Node:', info.nodeVersion);
    console.log('[Platform] Chrome:', info.chromeVersion);
    console.log('[Platform] Arch:', info.arch);
  }
};

/**
 * Window control functions (Electron only)
 */
export const windowControls = {
  /**
   * Minimize the window
   */
  minimize: async (): Promise<void> => {
    if (isElectron()) {
      await window.electronAPI?.minimizeWindow();
    }
  },

  /**
   * Maximize or restore the window
   */
  maximize: async (): Promise<void> => {
    if (isElectron()) {
      await window.electronAPI?.maximizeWindow();
    }
  },

  /**
   * Close the window
   */
  close: async (): Promise<void> => {
    if (isElectron()) {
      await window.electronAPI?.closeWindow();
    }
  },

  /**
   * Check if window is maximized
   */
  isMaximized: async (): Promise<boolean> => {
    if (isElectron()) {
      return await window.electronAPI?.isMaximized() ?? false;
    }
    return false;
  },

  /**
   * Listen for window state changes (fullscreen enter/leave)
   * Returns a cleanup function to remove the listener
   */
  onStateChanged: (callback: () => void): (() => void) => {
    if (isElectron() && window.electronAPI?.onWindowStateChanged) {
      return window.electronAPI.onWindowStateChanged(callback);
    }
    // Return no-op cleanup function for web
    return () => {};
  },
};

export default {
  isElectron,
  isWeb,
  getPlatform,
  isMac,
  isWindows,
  isLinux,
  getPlatformInfo,
  logPlatformInfo,
  windowControls,
};
